import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

// MUI Imports
import { Box, Button, CircularProgress, Drawer, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import SaveIcon from '@mui/icons-material/Save'
import AddTaskIcon from '@mui/icons-material/AddTask'

// Component Imports
import CampaignInformation from '../molecules/CampaignInformation'

// AI Template Components
import BusinessCompanyInfoSection from '../molecules/BusinessCompanyInfoSection'
import EmailPurposeTargetingSection from '../molecules/EmailPurposeTargetingSection'
import FileUploadSection from '../molecules/FileUploadSection'

import { useSDSsearchParams } from '../../../../../../src/store/searchDataStore'
import { useGlobalStoreActions, useGlobalStoreData } from '../../../../../../src/store/globalStore'

import { createCampaign, updateUser } from '../../../services/B2BServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useCountries } from '../../../../../campaigns/hooks/useCountries'

import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { LANGUAGES, TRADE_TYPES } from '../../../utils/campaignConstants'
import { getUserDetailsAndProcess } from '../../../../../services/DataServiceMethods'
import { extractEmailPurposeData, extractSenderCompanyData, filterEmptyValues } from '../../../utils/profileDataTransformers'

const CreateCampaignDrawer = (props) => {
  const { open, setOpen, b2bAccountId, fetchCampaigns, userGSD } = props

  const { t } = useTranslation()
  const { showError, showSuccess } = useToast()
  const { countries } = useCountries()

  const currentUser = useGlobalStoreData.user()
  const currentLang = useGlobalStoreData.language()
  const navigate = useNavigate()

  const searchParams = useSDSsearchParams()

  const [loading, setLoading] = useState(false)
  const [autofillLoading, setAutofillLoading] = useState(false)
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [showProfileNameInput, setShowProfileNameInput] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')

  const { setUserData } = useGlobalStoreActions()

  const userProfiles = useMemo(() => {
    if (!currentUser?.emailGeneratorParameters || currentUser.emailGeneratorParameters.length === 0) {
      return [
        {
          name: 'default',
          sender_company: currentUser?.senderCompany || {},
          email_purpose: currentUser?.emailPurpose || {}
        }]
    }
    return currentUser.emailGeneratorParameters
  }, [currentUser])

  const defaultValues = useMemo(() => ({
    name: '',
    importerCountryCode: (searchParams?.countryToExport && countries?.length)
      ? {
          label: countries.find((item) => item.countryCode === searchParams.countryToExport)?.label,
          countryCode: searchParams.countryToExport
        }
      : null,
    currentCountryCode: (searchParams?.yourCountry && countries?.length)
      ? {
          label: countries.find((item) => item.countryCode === searchParams.yourCountry)?.label,
          countryCode: searchParams.yourCountry
        }
      : null,
    hsCodes: searchParams?.hsCode ? searchParams.hsCode.split(',').map((item) => ({ label: item, value: item })) : [],
    productName: searchParams?.productDesc ? [{ value: searchParams.productDesc }] : [],
    productListOp: 'or',

    senderCompany: {
      name: '',
      industry: '',
      country: '',
      contact_person: '',
      contact_title: '',
      email: currentUser?.emailSettings?.emailUserName || '',
      key_products: [],
      website: '',
      phone: '',
      production_capacity: '',
      facilities: [],
      certifications: [],
      market_experience: [],
      company_values: []
    },
    emailPurpose: {
      target_market: '',
      trade_type: TRADE_TYPES.EXPORT,
      product_category: '',
      language: LANGUAGES.ENGLISH,
      specific_products: [],
      call_to_action: ''
    },
    attachments: {
      pdf_files: [],
      catalog_description: '',
      sample_availability: false
    }
  }), [searchParams, countries, currentUser])

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange'
  })

  const watchedFields = watch([
    'name',
    'hsCodes',
    'importerCountryCode',
    'productName',
    'currentCountryCode',
    'senderCompany.name',
    'senderCompany.industry',
    'senderCompany.country',
    'senderCompany.contact_person',
    'senderCompany.contact_title',
    'senderCompany.email',
    'senderCompany.key_products',
    'emailPurpose.target_market',
    'emailPurpose.trade_type',
    'emailPurpose.product_category',
    'emailPurpose.language'
  ])

  const isFormValid = useMemo(() => {
    const [
      name,
      hsCodes,
      importerCountryCode,
      productName,
      currentCountryCode,
      companyName,
      industry,
      country,
      contactPerson,
      contactTitle,
      email,
      keyProducts,
      targetMarket,
      tradeType,
      productCategory,
      language
    ] = watchedFields

    if (!name || !importerCountryCode || !currentCountryCode) {
      return false
    }

    if (!Array.isArray(hsCodes) || hsCodes.length === 0) {
      return false
    }

    if (!Array.isArray(productName) || productName.length === 0) {
      return false
    }

    if (!companyName || !industry || !country || !contactPerson || !contactTitle) {
      return false
    }

    if (!email) {
      return false
    }

    if (!keyProducts || !Array.isArray(keyProducts) || keyProducts.length === 0) {
      return false
    }

    if (!targetMarket || !tradeType || !productCategory || !language) {
      return false
    }

    return true
  }, [watchedFields])

  const handleClose = useCallback(() => {
    reset(defaultValues)
    setAttachedFiles([])
    setOpen(false)
  }, [reset, setOpen, defaultValues])

  const getLocalizedCountryName = useCallback((countryName) => {
    if (!countryName) return ''

    const currentCountries = currentLang === 'en' ? countryEN : countryTR
    const otherCountries = currentLang === 'en' ? countryTR : countryEN

    const foundInCurrent = currentCountries.find(c => c.countryName === countryName)
    if (foundInCurrent) {
      return countryName
    }

    const foundInOther = otherCountries.find(c => c.countryName === countryName)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) {
        return translatedCountry.countryName
      }
    }

    return countryName
  }, [currentLang])

  const handleAutofill = useCallback(() => {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      showError(t('mailTemplate.campaigns.modal.autofillDataNotAvailable'))
      return
    }

    if (!userProfiles || userProfiles.length === 0) {
      showError(t('mailTemplate.campaigns.modal.autofillDataNotAvailable'))
      return
    }

    setAutofillLoading(true)

    try {
      const transformArrayData = (data) => {
        if (!Array.isArray(data)) return []
        return data.map(item => {
          if (typeof item === 'string') {
            return { value: item }
          } else if (typeof item === 'object' && item !== null) {
            return { value: item.value || String(item) }
          }
          return { value: String(item) }
        })
      }

      const currentValues = watch()
      const selectedProfile = userProfiles[selectedProfileIndex]

      const autofillData = {
        ...currentValues,
        productName: selectedProfile?.productName?.length > 0 ? transformArrayData(selectedProfile.productName) : currentValues.productName,
        productListOp: selectedProfile?.productListOp || currentValues.productListOp || 'or',
        senderCompany: {
          ...currentValues.senderCompany,
          name: selectedProfile?.sender_company?.name || '',
          industry: selectedProfile?.sender_company?.industry || '',
          country: getLocalizedCountryName(selectedProfile?.sender_company?.country || ''),
          contact_person: selectedProfile?.sender_company?.contact_person || '',
          contact_title: selectedProfile?.sender_company?.contact_title || '',
          email: selectedProfile?.sender_company?.email || currentUser.emailSettings?.emailUserName || '',
          key_products: transformArrayData(selectedProfile?.sender_company?.key_products || []),
          website: selectedProfile?.sender_company?.website || '',
          phone: selectedProfile?.sender_company?.phone || '',
          production_capacity: selectedProfile?.sender_company?.production_capacity || '',
          facilities: transformArrayData(selectedProfile?.sender_company?.facilities || []),
          certifications: transformArrayData(selectedProfile?.sender_company?.certifications || []),
          market_experience: transformArrayData(selectedProfile?.sender_company?.market_experience || []),
          company_values: transformArrayData(selectedProfile?.sender_company?.company_values || [])
        },

        emailPurpose: {
          ...currentValues.emailPurpose,
          target_market: getLocalizedCountryName(selectedProfile?.email_purpose?.target_market || ''),
          trade_type: selectedProfile?.email_purpose?.trade_type || TRADE_TYPES.EXPORT,
          product_category: selectedProfile?.email_purpose?.product_category || '',
          language: selectedProfile?.email_purpose?.language || LANGUAGES.ENGLISH,
          specific_products: transformArrayData(selectedProfile?.email_purpose?.specific_products || []),
          call_to_action: selectedProfile?.email_purpose?.call_to_action || ''
        },

        attachments: {
          pdf_files: [],
          catalog_description: '',
          sample_availability: false
        }
      }

      reset(autofillData)
    } catch (error) {
      showError(t('mailTemplate.campaigns.modal.autofillDataLoadError'))
    } finally {
      setAutofillLoading(false)
    }
  }, [currentUser, userProfiles, selectedProfileIndex, showError, t, watch, reset, getLocalizedCountryName])

  const handleClearForm = useCallback(() => {
    const currentValues = watch()

    const clearedData = {
      ...defaultValues,
      name: currentValues.name,
      hsCodes: currentValues.hsCodes,
      importerCountryCode: currentValues.importerCountryCode,
      productName: currentValues.productName,
      currentCountryCode: currentValues.currentCountryCode
    }

    reset(clearedData)
  }, [reset, watch, defaultValues])

  const handleSaveAsProfile = useCallback(() => {
    if (!newProfileName.trim()) {
      showError(t('mailTemplate.users.modal.profileNameRequired'))
      return
    }

    if (userProfiles.some(p => p.name === newProfileName.trim())) {
      showError(t('mailTemplate.users.modal.profileNameExists'))
      return
    }

    const val = watch()
    const senderCompanyData = extractSenderCompanyData(val)
    const emailPurposeData = extractEmailPurposeData(val)

    const filteredSenderCompany = filterEmptyValues(senderCompanyData)
    const filteredEmailPurpose = filterEmptyValues(emailPurposeData)

    const newProfile = {
      name: newProfileName.trim(),
      productName: val.productName?.map(item => item.value || item) || [],
      productListOp: val.productListOp || 'or',
      sender_company: Object.keys(filteredSenderCompany).length > 0 ? filteredSenderCompany : {},
      email_purpose: Object.keys(filteredEmailPurpose).length > 0 ? filteredEmailPurpose : {}
    }

    const updatedProfiles = [...(currentUser?.emailGeneratorParameters || []), newProfile]

    setIsSavingProfile(true)
    updateUser(currentUser?.ownedB2bAccountId, { emailGeneratorParameters: updatedProfiles }).then(() => {
      getUserDetailsAndProcess(true, userGSD, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.toastSuccess'))
      setShowProfileNameInput(false)
      setNewProfileName('')
    }).catch((err) => {
      err && showError(t('mailTemplate.users.modal.toastErr'))
    }).finally(() => {
      setIsSavingProfile(false)
    })
  }, [newProfileName, userProfiles, watch, currentUser, userGSD, setUserData, showError, showSuccess, t])

  const onSubmit = useCallback((val) => {
    const payload = {
      name: val.name,
      importerCountryCodes: [val.importerCountryCode.countryCode],
      currentCountryCode: val.currentCountryCode.countryCode,
      hsCodes: val.hsCodes.map((x) => x.value),
      productName: val.productName?.map(item => item.value || item) || [],
      productListOp: val.productListOp || 'or',
      b2bAccountId,
      firmSelectionAssist: true,
      emailContentAssist: true,
      sender_company: {
        ...val.senderCompany,
        key_products: val.senderCompany?.key_products?.map(item => item.value || item) || [],
        facilities: val.senderCompany?.facilities?.map(item => item.value || item) || [],
        certifications: val.senderCompany?.certifications?.map(item => item.value || item) || [],
        market_experience: val.senderCompany?.market_experience?.map(item => item.value || item) || [],
        company_values: val.senderCompany?.company_values?.map(item => item.value || item) || []
      },
      email_purpose: {
        ...val.emailPurpose,
        specific_products: val.emailPurpose?.specific_products?.map(item => item.value || item) || []
      }
    }

    setLoading(true)
    createCampaign(payload, attachedFiles).then((response) => {
      const campaignId = response?.data?.id || response?.data
      fetchCampaigns()
      handleClose()
      getUserDetailsAndProcess(true, userGSD, setUserData, true)

      if (campaignId) {
        navigate(`/${currentLang}/campaigns/detail?b2bAccountId=${b2bAccountId}&b2bCampaignId=${campaignId}`)
      }
    }).catch((err) => {
      const isDuplicateName = err?.response?.status === 409
      const errorMessage = isDuplicateName
        ? t('mailTemplate.campaigns.modal.duplicateNameErr')
        : t('mailTemplate.campaigns.modal.toastErr')

      err && showError(errorMessage)
    }).finally(() => {
      setLoading(false)
    })
  }, [b2bAccountId, attachedFiles, fetchCampaigns, handleClose, setUserData, showError, t, userGSD])

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600, md: 700 },
          maxWidth: '90vw',
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          backgroundColor: 'white',
          p: 3,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <IconButton
            onClick={handleClose}
            sx={{
              mr: 2,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ArrowBackIcon/>
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {t('mailTemplate.campaigns.modal.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('mailTemplate.campaigns.modal.subTitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {userProfiles.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="profile-select-label">{t('mailTemplate.campaigns.modal.profile')}</InputLabel>
                <Select
                  labelId="profile-select-label"
                  value={selectedProfileIndex}
                  label={t('mailTemplate.campaigns.modal.profile')}
                  onChange={(e) => setSelectedProfileIndex(e.target.value)}
                  disabled={loading || autofillLoading}
                  sx={{ borderRadius: 2 }}
                >
                  {userProfiles.map((profile, index) => (
                    <MenuItem key={index} value={index}>
                      {profile.name || `Profile ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Tooltip title={t('mailTemplate.campaigns.modal.autofillTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={autofillLoading ? <CircularProgress size={16}/> : <AutoFixHighIcon/>}
                onClick={handleAutofill}
                disabled={autofillLoading || loading}
                sx={{
                  minWidth: 'auto',
                  borderRadius: 2,
                  px: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                {t('mailTemplate.campaigns.modal.autofill')}
              </Button>
            </Tooltip>
            <Tooltip title={t('mailTemplate.campaigns.modal.clearTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearAllIcon/>}
                onClick={handleClearForm}
                disabled={loading}
                sx={{
                  minWidth: 'auto',
                  borderRadius: 2,
                  px: 2,
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {t('mailTemplate.campaigns.modal.clear')}
              </Button>
            </Tooltip>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon/>
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#f8fafc', pb: 2 }}>
            <Box sx={{ p: 3 }}>
              <CampaignInformation
                control={control}
                errors={errors}
                countries={countries}
              />

              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  {t('mailTemplate.wizard.ai.businessCompanyInfo')}
                </Typography>

                <BusinessCompanyInfoSection
                  control={control}
                  fieldPrefix="senderCompany"
                  requiredFieldsOnly={false}
                />
                {/* </Paper> */}

                {/* <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3 }}> */}
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3, mt: 3 }}>
                  {t('mailTemplate.wizard.ai.emailPurpose')}
                </Typography>

                <EmailPurposeTargetingSection
                  control={control}
                  fieldPrefix="emailPurpose"
                  requiredFieldsOnly={false}
                />

                <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                  {!showProfileNameInput
                    ? (
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<SaveIcon/>}
                        onClick={() => setShowProfileNameInput(true)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('mailTemplate.campaigns.modal.saveAsProfile')}
                      </Button>
                      )
                    : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder={t('mailTemplate.users.modal.profileNamePlaceholder')}
                          value={newProfileName}
                          onChange={(e) => setNewProfileName(e.target.value)}
                          sx={{ flex: 1, minWidth: 200 }}
                          autoFocus
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={isSavingProfile ? <CircularProgress size={16}/> : <AddTaskIcon/>}
                            onClick={handleSaveAsProfile}
                            disabled={isSavingProfile || !newProfileName.trim()}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' }
                            }}
                          >
                            {t('mailTemplate.users.modal.save')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setShowProfileNameInput(false)
                              setNewProfileName('')
                            }}
                            disabled={isSavingProfile}
                            sx={{ borderRadius: 1.5, textTransform: 'none' }}
                          >
                            {t('mailTemplate.campaigns.modal.cancel')}
                          </Button>
                        </Box>
                      </Box>
                      )}
                </Box>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  {t('mailTemplate.campaigns.modal.attachments')}
                </Typography>

                <FileUploadSection
                  files={attachedFiles}
                  onFilesChange={setAttachedFiles}
                />
              </Paper>
            </Box>
          </Box>

          <Paper sx={{
            p: 3,
            m: 0,
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
            position: 'sticky',
            bottom: 0,
            zIndex: 1
          }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClose}
              sx={{
                minWidth: 100,
                height: 44,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {t('mailTemplate.campaigns.modal.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !isFormValid}
              startIcon={loading ? <CircularProgress sx={{ color: '#fff' }} size={20}/> : null}
              sx={{
                minWidth: 120,
                height: 44,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #0fc6b9 30%, #0ba89e 90%)',
                boxShadow: '0 3px 12px rgba(15, 198, 185, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0eb3a6 30%, #0a998b 90%)',
                  boxShadow: '0 4px 16px rgba(15, 198, 185, 0.5)'
                },
                '&:disabled': {
                  background: 'rgba(15, 198, 185, 0.6)',
                  color: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
              {t('mailTemplate.campaigns.modal.submit')}
            </Button>
          </Paper>
        </form>
      </Box>
    </Drawer>
  )
}

export default CreateCampaignDrawer
